import { useState, useEffect, useRef, useCallback } from "react";
import { axiosInstance } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Scan, Search, X, CheckCircle2, AlertTriangle, Trash2, FileText,
  FileSpreadsheet, Eye, Loader2, Pause, Play, ListPlus, History, ClipboardList,
} from "lucide-react";
import { toast } from "sonner";
import { getErrorMessage } from "../utils/errorHandler";

const fmtCRC = (n) =>
  `₡${(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signedCRC = (n) =>
  `${n > 0 ? "+" : n < 0 ? "-" : ""}₡${Math.abs(n || 0).toLocaleString("es-CR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function InventoryAudit({ boxId, currentUser }) {
  const [audits, setAudits] = useState([]);
  const [activeAudit, setActiveAudit] = useState(null);
  const [diff, setDiff] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [scanCode, setScanCode] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [newAuditName, setNewAuditName] = useState("");
  const [newAuditNotes, setNewAuditNotes] = useState("");
  const [applyOpen, setApplyOpen] = useState(false);
  const [applyPassword, setApplyPassword] = useState("");
  const [applyReason, setApplyReason] = useState("");
  const [applying, setApplying] = useState(false);
  const [zeroUncounted, setZeroUncounted] = useState(true);
  const [viewAdjOpen, setViewAdjOpen] = useState(false);
  const [auditAdjustments, setAuditAdjustments] = useState(null);
  const [showHistory, setShowHistory] = useState(false);
  const scanInputRef = useRef(null);

  const isMaster = currentUser?.role === "master";

  // Load audits + products on mount/box change
  const loadAudits = useCallback(async () => {
    if (!boxId) return;
    try {
      const r = await axiosInstance.get(`/inventory-audits?box_id=${boxId}`);
      setAudits(r.data.audits || []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al cargar auditorías"));
    }
  }, [boxId]);

  const loadProducts = useCallback(async () => {
    if (!boxId) return;
    try {
      const r = await axiosInstance.get(`/inventory/products?box_id=${boxId}`);
      setProducts(r.data.products || []);
    } catch {
      // silent
    }
  }, [boxId]);

  useEffect(() => {
    loadAudits();
    loadProducts();
  }, [loadAudits, loadProducts]);

  const fetchDiff = useCallback(async (auditId) => {
    try {
      const r = await axiosInstance.get(`/inventory-audits/${auditId}/diff`);
      setDiff(r.data);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al cargar diferencias"));
    }
  }, []);

  const openAudit = useCallback(async (audit) => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/inventory-audits/${audit.id}`);
      setActiveAudit(r.data.audit);
      await fetchDiff(audit.id);
      setTimeout(() => scanInputRef.current?.focus(), 100);
    } finally {
      setLoading(false);
    }
  }, [fetchDiff]);

  const handleCreateAudit = async () => {
    if (!boxId) {
      toast.error("Selecciona una caja primero");
      return;
    }
    try {
      const r = await axiosInstance.post(`/inventory-audits`, {
        box_id: boxId,
        name: newAuditName.trim(),
        notes: newAuditNotes.trim(),
      });
      toast.success("Sesión de inventario creada");
      setCreateOpen(false);
      setNewAuditName("");
      setNewAuditNotes("");
      await loadAudits();
      await openAudit(r.data.audit);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al crear sesión"));
    }
  };

  const handleScan = async (e) => {
    e?.preventDefault?.();
    const code = scanCode.trim();
    if (!code || !activeAudit) return;
    try {
      const r = await axiosInstance.post(`/inventory-audits/${activeAudit.id}/scan`, {
        code,
        qty: 1,
        mode: "increment",
      });
      toast.success(`+1: ${r.data.product.name} (total: ${r.data.count.counted_qty})`);
      setScanCode("");
      await fetchDiff(activeAudit.id);
      const aud = await axiosInstance.get(`/inventory-audits/${activeAudit.id}`);
      setActiveAudit(aud.data.audit);
    } catch (err) {
      toast.error(getErrorMessage(err, "Producto no encontrado"));
      setScanCode("");
    } finally {
      scanInputRef.current?.focus();
    }
  };

  const handleSetCount = async (productId, qty) => {
    if (!activeAudit) return;
    const n = parseInt(qty, 10);
    if (isNaN(n) || n < 0) return;
    try {
      await axiosInstance.put(`/inventory-audits/${activeAudit.id}/count`, {
        product_id: productId,
        counted_qty: n,
        mode: "set",
      });
      await fetchDiff(activeAudit.id);
      const aud = await axiosInstance.get(`/inventory-audits/${activeAudit.id}`);
      setActiveAudit(aud.data.audit);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al guardar conteo"));
    }
  };

  const handleRemoveCount = async (productId) => {
    if (!activeAudit) return;
    try {
      await axiosInstance.delete(`/inventory-audits/${activeAudit.id}/count/${productId}`);
      await fetchDiff(activeAudit.id);
      const aud = await axiosInstance.get(`/inventory-audits/${activeAudit.id}`);
      setActiveAudit(aud.data.audit);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error"));
    }
  };

  const handleApply = async () => {
    if (!activeAudit) return;
    if (!applyPassword) {
      toast.error("Ingresa tu contraseña de MASTER");
      return;
    }
    setApplying(true);
    try {
      const r = await axiosInstance.post(`/inventory-audits/${activeAudit.id}/apply`, {
        password: applyPassword,
        reason: applyReason,
        zero_uncounted: zeroUncounted,
      });
      toast.success(r.data.message || "Ajustes aplicados");
      setApplyOpen(false);
      setApplyPassword("");
      setApplyReason("");
      // Show adjustments preview
      const adj = await axiosInstance.get(`/inventory-audits/${activeAudit.id}/adjustments`);
      setAuditAdjustments(adj.data);
      setViewAdjOpen(true);
      setActiveAudit(null);
      setDiff(null);
      await loadAudits();
      await loadProducts();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al aplicar ajustes"));
    } finally {
      setApplying(false);
    }
  };

  const handleCancelAudit = async (auditId) => {
    if (!window.confirm("¿Cancelar esta sesión de inventario? Se perderán los conteos guardados.")) return;
    try {
      await axiosInstance.delete(`/inventory-audits/${auditId}`);
      toast.success("Sesión cancelada");
      if (activeAudit?.id === auditId) {
        setActiveAudit(null);
        setDiff(null);
      }
      await loadAudits();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error"));
    }
  };

  const handleZeroUncountedRetro = async (auditId, auditName) => {
    const password = window.prompt(
      `Poner en 0 TODOS los productos NO revisados en "${auditName || auditId.slice(0, 8)}".\n\n` +
      `Esto pondrá quantity=0 a cualquier producto de la caja que no aparezca en los conteos ` +
      `de esta auditoría. Es DEFINITIVO.\n\nContraseña MASTER:`
    );
    if (!password) return;
    try {
      const r = await axiosInstance.post(
        `/inventory-audits/${auditId}/zero-uncounted-retro`,
        { password, reason: "Zero-out retroactivo" }
      );
      toast.success(`${r.data.products_updated} productos puestos en 0`);
      await loadAudits();
    } catch (e) {
      toast.error(getErrorMessage(e, "Error al aplicar zero-out"));
    }
  };

  const handleViewAuditAdjustments = async (auditId) => {
    try {
      const r = await axiosInstance.get(`/inventory-audits/${auditId}/adjustments`);
      setAuditAdjustments(r.data);
      setViewAdjOpen(true);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error"));
    }
  };

  const handleDownloadAdj = async (auditId, kind) => {
    try {
      const path = kind === "pdf" ? "pdf" : "excel";
      const r = await axiosInstance.get(`/inventory-audits/${auditId}/adjustments/${path}`);
      const b64 = kind === "pdf" ? r.data.pdf_base64 : r.data.excel_base64;
      const mime = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const link = document.createElement("a");
      link.href = `data:${mime};base64,${b64}`;
      link.download = r.data.filename;
      link.click();
      toast.success(`${kind.toUpperCase()} descargado`);
    } catch (e) {
      toast.error(getErrorMessage(e, "Error"));
    }
  };

  // Quick search products to count manually
  const filteredProducts = searchTerm.trim()
    ? products.filter(
        (p) =>
          p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          p.code?.toLowerCase().includes(searchTerm.toLowerCase())
      ).slice(0, 50)
    : products.slice(0, 100);

  const countsMap = {};
  (activeAudit?.counts || []).forEach((c) => (countsMap[c.product_id] = c.counted_qty));

  if (!isMaster) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <AlertTriangle className="w-10 h-10 mx-auto text-amber-500 mb-3" />
          <p className="text-slate-600">Solo el usuario MASTER puede acceder al módulo de Inventario Físico.</p>
        </CardContent>
      </Card>
    );
  }

  // ====== ACTIVE AUDIT VIEW ======
  if (activeAudit) {
    const t = diff?.totals || {};
    return (
      <div className="space-y-4" data-testid="inventory-audit-active">
        <Card className="border-blue-300 bg-gradient-to-r from-blue-50 to-indigo-50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <CardTitle className="text-lg flex items-center gap-2">
                  <ClipboardList className="w-5 h-5 text-blue-700" />
                  {activeAudit.name}
                </CardTitle>
                <p className="text-xs text-slate-500 mt-1">
                  Caja: {activeAudit.box_name} · Iniciada: {(activeAudit.created_at || "").substring(0, 16).replace("T", " ")}
                </p>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => { setActiveAudit(null); setDiff(null); loadAudits(); }} data-testid="audit-pause-btn">
                  <Pause className="w-4 h-4 mr-1" /> Pausar
                </Button>
                <Button variant="destructive" size="sm" onClick={() => handleCancelAudit(activeAudit.id)} data-testid="audit-cancel-btn">
                  <X className="w-4 h-4 mr-1" /> Cancelar
                </Button>
                <Button
                  size="sm"
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => setApplyOpen(true)}
                  disabled={!t.products_with_diff}
                  data-testid="audit-apply-btn"
                >
                  <CheckCircle2 className="w-4 h-4 mr-1" /> Aplicar Ajustes ({t.products_with_diff || 0})
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Stat label="Productos contados" value={t.products_counted || 0} color="blue" />
              <Stat label="Con diferencia" value={t.products_with_diff || 0} color="orange" />
              <Stat label="Faltante (costo)" value={fmtCRC(t.total_faltante_cost || 0)} color="red" />
              <Stat label="Sobrante (costo)" value={fmtCRC(t.total_sobrante_cost || 0)} color="green" />
            </div>
          </CardContent>
        </Card>

        {/* Scan input */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Scan className="w-4 h-4 text-blue-600" />
              Escáner / Código manual
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleScan} className="flex gap-2">
              <Input
                ref={scanInputRef}
                value={scanCode}
                onChange={(e) => setScanCode(e.target.value)}
                placeholder="Escanea o escribe código y presiona Enter (suma 1)"
                className="font-mono"
                autoFocus
                data-testid="audit-scan-input"
              />
              <Button type="submit" data-testid="audit-scan-submit">
                <Plus className="w-4 h-4 mr-1" /> Sumar
              </Button>
            </form>
            <p className="text-xs text-slate-500 mt-2">
              Cada Enter suma <b>+1</b> al producto. Para fijar una cantidad exacta, edítala en la tabla manual de abajo.
            </p>
          </CardContent>
        </Card>

        {/* Diff table */}
        {diff && diff.rows && diff.rows.length > 0 && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <ListPlus className="w-4 h-4 text-blue-600" />
                Conteo Actual ({diff.rows.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100 sticky top-0">
                    <tr>
                      <th className="p-2 text-left">Código</th>
                      <th className="p-2 text-left">Producto</th>
                      <th className="p-2 text-right">Sistema</th>
                      <th className="p-2 text-right">Contado</th>
                      <th className="p-2 text-right">Diferencia</th>
                      <th className="p-2 text-right">Costo Unit.</th>
                      <th className="p-2 text-right">Impacto</th>
                      <th className="p-2 text-center">Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diff.rows.map((r, i) => {
                      const dz = r.difference || 0;
                      const cls = dz === 0 ? "" : dz < 0 ? "bg-red-50" : "bg-green-50";
                      const txc = dz === 0 ? "" : dz < 0 ? "text-red-700 font-bold" : "text-green-700 font-bold";
                      return (
                        <tr key={r.product_id} className={`border-b ${cls}`}>
                          <td className="p-2 font-mono">{r.code}</td>
                          <td className="p-2">{r.name}</td>
                          <td className="p-2 text-right">{r.system_qty}</td>
                          <td className="p-2 text-right">
                            <Input
                              type="number"
                              min="0"
                              value={r.counted_qty ?? ""}
                              onChange={(e) => handleSetCount(r.product_id, e.target.value)}
                              className="h-7 w-20 text-right ml-auto"
                            />
                          </td>
                          <td className={`p-2 text-right ${txc}`}>{dz > 0 ? `+${dz}` : dz}</td>
                          <td className="p-2 text-right font-mono">{fmtCRC(r.cost_unit)}</td>
                          <td className={`p-2 text-right font-mono ${txc}`}>{signedCRC(r.cost_impact)}</td>
                          <td className="p-2 text-center">
                            <button
                              onClick={() => handleRemoveCount(r.product_id)}
                              className="text-red-600 hover:text-red-800"
                              title="Quitar conteo"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Manual product picker */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <Search className="w-4 h-4 text-blue-600" />
              Búsqueda manual de productos
            </CardTitle>
          </CardHeader>
          <CardContent>
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Buscar por nombre o código..."
              className="mb-3"
              data-testid="audit-product-search"
            />
            <div className="overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-100 sticky top-0">
                  <tr>
                    <th className="p-2 text-left">Código</th>
                    <th className="p-2 text-left">Producto</th>
                    <th className="p-2 text-right">Sistema</th>
                    <th className="p-2 text-right">Contado</th>
                    <th className="p-2 text-center">Acción</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredProducts.map((p) => {
                    const counted = countsMap[p.id];
                    return (
                      <tr key={p.id} className="border-b">
                        <td className="p-2 font-mono">{p.code}</td>
                        <td className="p-2">{p.name}</td>
                        <td className="p-2 text-right">{p.quantity || 0}</td>
                        <td className="p-2 text-right">
                          <Input
                            type="number"
                            min="0"
                            placeholder={counted == null ? "—" : ""}
                            defaultValue={counted ?? ""}
                            key={`${p.id}-${counted ?? "empty"}`}
                            onBlur={(e) => {
                              const v = e.target.value;
                              if (v === "" || v == null) return;
                              handleSetCount(p.id, v);
                            }}
                            className="h-7 w-20 text-right ml-auto"
                          />
                        </td>
                        <td className="p-2 text-center">
                          <Button
                            size="xs"
                            variant="outline"
                            className="h-6 px-2 text-xs"
                            onClick={() =>
                              handleSetCount(p.id, (counted || 0) + 1)
                            }
                          >
                            +1
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Apply modal */}
        <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
          <DialogContent data-testid="audit-apply-dialog">
            <DialogHeader>
              <DialogTitle>Aplicar Ajustes de Inventario</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 p-3 rounded text-sm">
                <p className="text-amber-800 font-semibold flex items-center gap-1">
                  <AlertTriangle className="w-4 h-4" /> Esta acción es definitiva
                </p>
                <p className="text-amber-700 text-xs mt-1">
                  Se actualizarán <b>{t.products_with_diff || 0}</b> productos contados para que coincidan con la cuenta física.
                  El impacto monetario es <b>{signedCRC(t.total_cost_impact || 0)}</b>.
                  {zeroUncounted && (
                    <><br />⚠️ Además, todos los productos <b>NO revisados</b> con stock &gt; 0 se pondrán en <b>0</b>.</>
                  )}
                </p>
              </div>
              <div>
                <Label>Motivo del ajuste (opcional)</Label>
                <Input
                  value={applyReason}
                  onChange={(e) => setApplyReason(e.target.value)}
                  placeholder="Ej: Inventario físico mensual, mermas detectadas..."
                  data-testid="audit-apply-reason"
                />
              </div>
              <div className="rounded border-2 border-red-200 bg-red-50 p-3">
                <label className="flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={zeroUncounted}
                    onChange={(e) => setZeroUncounted(e.target.checked)}
                    className="mt-0.5"
                    data-testid="audit-zero-uncounted"
                  />
                  <div className="text-sm">
                    <div className="font-semibold text-red-800">
                      Poner en <b>0</b> los productos NO revisados
                    </div>
                    <div className="text-xs text-red-700 mt-0.5">
                      Si el producto tiene stock en el sistema pero no fue escaneado/contado en este inventario físico, se asume que <b>no existe</b> y se pondrá en 0. Recomendado para tener el reporte real.
                    </div>
                  </div>
                </label>
              </div>
              <div>
                <Label>Contraseña MASTER *</Label>
                <Input
                  type="password"
                  value={applyPassword}
                  onChange={(e) => setApplyPassword(e.target.value)}
                  data-testid="audit-apply-password"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setApplyOpen(false)}>Cancelar</Button>
              <Button
                onClick={handleApply}
                disabled={applying || !applyPassword}
                className="bg-green-600 hover:bg-green-700"
                data-testid="audit-apply-confirm"
              >
                {applying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-1" />}
                Confirmar y Aplicar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <AuditAdjustmentsViewer open={viewAdjOpen} onClose={() => setViewAdjOpen(false)} data={auditAdjustments} onDownload={handleDownloadAdj} />
      </div>
    );
  }

  // ====== AUDIT LIST VIEW ======
  return (
    <div className="space-y-4" data-testid="inventory-audit-list">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <ClipboardList className="w-5 h-5 text-blue-700" />
            Inventario Físico
          </h2>
          <p className="text-xs text-slate-500">Sesiones de conteo y ajustes (solo MASTER)</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowHistory(!showHistory)} data-testid="audit-toggle-history">
            <History className="w-4 h-4 mr-1" /> {showHistory ? "Ocultar historial" : "Ver historial"}
          </Button>
          <Button onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="audit-create-btn">
            <Plus className="w-4 h-4 mr-1" /> Nueva Sesión
          </Button>
        </div>
      </div>

      {/* Active sessions */}
      {audits.filter((a) => a.status === "active").length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Sesiones Activas (en curso)</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {audits.filter((a) => a.status === "active").map((a) => (
              <div key={a.id} className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded">
                <div>
                  <p className="font-semibold text-sm">{a.name}</p>
                  <p className="text-xs text-slate-500">
                    Iniciada: {(a.created_at || "").substring(0, 16).replace("T", " ")} · {a.counts?.length || 0} productos contados
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={() => openAudit(a)} data-testid={`audit-resume-${a.id}`}>
                    <Play className="w-4 h-4 mr-1" /> Continuar
                  </Button>
                  <Button size="sm" variant="destructive" onClick={() => handleCancelAudit(a.id)}>
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* History */}
      {showHistory && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm">Historial de Auditorías</CardTitle>
          </CardHeader>
          <CardContent>
            {audits.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 text-center">Sin auditorías aún.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="p-2 text-left">Nombre</th>
                      <th className="p-2 text-left">Estado</th>
                      <th className="p-2 text-left">Creado</th>
                      <th className="p-2 text-left">Aplicado</th>
                      <th className="p-2 text-right">Ajustes</th>
                      <th className="p-2 text-center">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {audits.map((a) => (
                      <tr key={a.id} className="border-b">
                        <td className="p-2">{a.name}</td>
                        <td className="p-2">
                          <Badge
                            className={
                              a.status === "active" ? "bg-blue-100 text-blue-700 border-blue-300"
                                : a.status === "completed" ? "bg-green-100 text-green-700 border-green-300"
                                : "bg-slate-100 text-slate-600 border-slate-300"
                            }
                          >
                            {a.status === "active" ? "Activa" : a.status === "completed" ? "Completada" : "Cancelada"}
                          </Badge>
                        </td>
                        <td className="p-2">{(a.created_at || "").substring(0, 10)}</td>
                        <td className="p-2">{(a.applied_at || "").substring(0, 10)}</td>
                        <td className="p-2 text-right">{a.adjustments_count || 0}</td>
                        <td className="p-2 text-center">
                          {a.status === "completed" && (
                            <div className="flex gap-1 justify-center">
                              <Button size="xs" variant="outline" className="h-6 px-2 text-xs" onClick={() => handleViewAuditAdjustments(a.id)}>
                                <Eye className="w-3 h-3" />
                              </Button>
                              <Button size="xs" variant="outline" className="h-6 px-2 text-xs" onClick={() => handleDownloadAdj(a.id, "pdf")}>
                                <FileText className="w-3 h-3" />
                              </Button>
                              <Button size="xs" variant="outline" className="h-6 px-2 text-xs" onClick={() => handleDownloadAdj(a.id, "excel")}>
                                <FileSpreadsheet className="w-3 h-3" />
                              </Button>
                              {isMaster && !a.zero_uncounted_retro_at && (
                                <Button
                                  size="xs"
                                  variant="outline"
                                  className="h-6 px-2 text-xs bg-red-50 text-red-700 border-red-300 hover:bg-red-100"
                                  onClick={() => handleZeroUncountedRetro(a.id, a.name)}
                                  title="Poner en 0 los productos NO revisados de esta auditoría"
                                  data-testid={`audit-zero-retro-${a.id}`}
                                >
                                  0️⃣
                                </Button>
                              )}
                              {a.zero_uncounted_retro_at && (
                                <span
                                  className="text-[10px] text-slate-400 px-1 self-center"
                                  title={`Retroactivo aplicado ${a.zero_uncounted_retro_at}`}
                                >
                                  ✓0
                                </span>
                              )}
                            </div>
                          )}
                          {a.status === "active" && (
                            <Button size="xs" onClick={() => openAudit(a)} className="h-6 px-2 text-xs">
                              <Play className="w-3 h-3" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Create modal */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent data-testid="audit-create-dialog">
          <DialogHeader>
            <DialogTitle>Nueva Sesión de Inventario Físico</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Nombre (opcional)</Label>
              <Input
                value={newAuditName}
                onChange={(e) => setNewAuditName(e.target.value)}
                placeholder="Ej: Inventario mensual abril"
              />
            </div>
            <div>
              <Label>Notas (opcional)</Label>
              <Input
                value={newAuditNotes}
                onChange={(e) => setNewAuditNotes(e.target.value)}
                placeholder="Detalles adicionales..."
              />
            </div>
            <p className="text-xs text-slate-500">
              Se usará la caja seleccionada actualmente. Podrás escanear códigos, buscar productos manualmente y guardar el progreso para retomar después.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreateAudit} className="bg-blue-600 hover:bg-blue-700">Crear y empezar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AuditAdjustmentsViewer open={viewAdjOpen} onClose={() => setViewAdjOpen(false)} data={auditAdjustments} onDownload={handleDownloadAdj} />
    </div>
  );
}

function Stat({ label, value, color }) {
  const colorMap = {
    blue: "bg-blue-100 text-blue-800 border-blue-300",
    orange: "bg-orange-100 text-orange-800 border-orange-300",
    red: "bg-red-100 text-red-800 border-red-300",
    green: "bg-green-100 text-green-800 border-green-300",
  };
  return (
    <div className={`rounded-lg border p-3 ${colorMap[color] || "bg-slate-100"}`}>
      <p className="text-xs uppercase font-semibold opacity-70">{label}</p>
      <p className="text-xl font-bold mt-1">{value}</p>
    </div>
  );
}

function AuditAdjustmentsViewer({ open, onClose, data, onDownload }) {
  if (!data) return null;
  const t = data.totals || {};
  const adjustments = data.adjustments || [];
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Ajustes Aplicados — {data.audit?.name}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => onDownload(data.audit.id, "pdf")}>
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button size="sm" variant="outline" onClick={() => onDownload(data.audit.id, "excel")}>
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2 text-xs">
            <Stat label="Ajustes" value={t.count || 0} color="blue" />
            <Stat label="Δ Unidades" value={t.total_diff_units || 0} color="orange" />
            <Stat label="Faltante" value={fmtCRC(t.total_faltante_cost || 0)} color="red" />
            <Stat label="Sobrante" value={fmtCRC(t.total_sobrante_cost || 0)} color="green" />
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-3 rounded border">
            <div><b>Caja:</b> {data.audit?.box_name}</div>
            <div><b>Aplicado por:</b> {data.audit?.applied_by}</div>
            <div><b>Fecha:</b> {(data.audit?.applied_at || "").substring(0, 19).replace("T", " ")}</div>
            <div><b>Motivo:</b> {data.audit?.reason || "—"}</div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead className="bg-slate-200">
                <tr>
                  <th className="p-2 text-left">Código</th>
                  <th className="p-2 text-left">Producto</th>
                  <th className="p-2 text-right">Antes</th>
                  <th className="p-2 text-right">Después</th>
                  <th className="p-2 text-right">Δ</th>
                  <th className="p-2 text-right">Costo</th>
                  <th className="p-2 text-right">Impacto</th>
                </tr>
              </thead>
              <tbody>
                {adjustments.map((a, i) => {
                  const dz = a.difference;
                  const cls = dz < 0 ? "bg-red-50 text-red-700" : dz > 0 ? "bg-green-50 text-green-700" : "";
                  return (
                    <tr key={i} className="border-b">
                      <td className="p-2 font-mono">{a.product_code}</td>
                      <td className="p-2">{a.product_name}</td>
                      <td className="p-2 text-right">{a.stock_before}</td>
                      <td className="p-2 text-right">{a.stock_after}</td>
                      <td className={`p-2 text-right font-bold ${cls}`}>{dz > 0 ? `+${dz}` : dz}</td>
                      <td className="p-2 text-right font-mono">{fmtCRC(a.cost_unit)}</td>
                      <td className={`p-2 text-right font-mono font-bold ${cls}`}>{signedCRC(a.cost_impact)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
